// @flow
import styled, { css, keyframes } from "styled-components";
import { media } from "@nested/brand";

// delay between appearance of each node in milliseconds. Tweaking this parameters might require
// tweaking duration of individual animations. Delay is proportional to position of the Node in the graph.
export const BASE_DELAY = 3600;

// this should always match for Dot and NodeText components
const SLIDE_UP_DELAY = 500;

// This controls delay before Ellipsis starts to animate
const ELLIPSIS_DELAY = 700;

// This controls delay for appearance of SubMessage
const SUB_MESSAGE_DELAY = 2200;

const checkInboxFadeIn = keyframes`
to {
  opacity: 1;
  transform: translateY(0);
}
`;

export const CheckInboxWrapper = styled.section`
  position: relative;
  max-width: 1440px;
  opacity: 0;
  transform: translateY(10px);
  background-color: ${({ theme }) => theme.palette.hague};
  padding-top: 30px;
  text-align: center;
  ${media.desktop`
    padding: 60px 40px 60px 40px;
    margin-top: 15px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  `}
  ${media.maxDesktop`
    padding: 90px 150px 90px 150px;
    margin-top: 25px;
  `}
  animation: ${checkInboxFadeIn} 0.5s ease-out 1 forwards;
  animation-delay: ${({ delay }) => `${delay}ms`};
`;

const dotFadeIn = keyframes`
to {
  opacity: 1;
  transform: translateY(0);
}
`;

const dotStyle = css`
  width: 11px;
  height: 11px;
  background: ${({ theme }) => theme.palette.terracotta150};
  border-radius: 50%;
  position: absolute;
  top: 8px;
  opacity: 0;
  transform: translateY(10px);
  animation: ${dotFadeIn} 0.4s ease-out 1 forwards;
  animation-delay: ${({ position }) =>
    position && `${position * BASE_DELAY + SLIDE_UP_DELAY}ms`};
`;

const pulsate = keyframes`
0% {
  transform: scale(0.1, 0.1); 
  opacity: 0.0;
}
50% {
  opacity: 1.0;
}
100% {
  transform: scale(1.2, 1.2); 
  opacity: 0.0;
}`;

const ringStyle = css`
  border: 2px solid ${({ theme }) => theme.palette.terracotta150};
  border-radius: 30px;
  height: 20.5px;
  width: 20.5px;
  position: absolute;
  left: -7px;
  top: 1px;
  animation: ${pulsate} 1s ease-out 3;
  animation-delay: ${({ position }) =>
    (position && `${position * BASE_DELAY + 2 * SLIDE_UP_DELAY}ms`) || "500ms"};
  opacity: 0;
`;

export const Dot = ({
  position,
  className,
}: {
  position?: number,
  className?: string,
}) => (
  <div className={className}>
    <div css="position: relative">
      <div position={position} css={dotStyle}></div>
      <div position={position} css={ringStyle}></div>
    </div>
  </div>
);

const ellipsis = keyframes`
to {
  width: 16px;
} 
`;

const ellipsisDesktop = keyframes`
  to {
    width: 21px;
  } 
`;

export const Ellipsis = styled.span`
  color: ${({ theme }) => theme.palette.hague100};
  font-weight: 500;
  position: absolute;
  &:after {
    display: inline-block;
    overflow: hidden;
    vertical-align: bottom;
    animation: ${ellipsis} steps(4, end) 3600ms 1;
    animation-delay: ${({ position }) =>
      position && `${position * BASE_DELAY + ELLIPSIS_DELAY}ms`};
    ${media.desktop`
      animation: ${ellipsisDesktop} steps(4, end) 3600ms 1;
      animation-delay: ${({ position }) =>
        position && `${position * BASE_DELAY + ELLIPSIS_DELAY}ms`};
    `}
    content: "\\2026";
    width: 0px;
  }
`;

export const GraphWrapper = styled.section`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: auto;
  max-width: 1440px;
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 25px;
  ${media.desktop`
    margin: auto;
    width: fit-content;
    padding-top: 50px;
  `}
`;

export const Message = styled.p`
  margin: 0;
  padding: 0;
  display: inline-block;
  color: ${({ theme }) => theme.palette.hague100};
  padding-left: 28px;
`;

const NodeContentFadeIn = keyframes`
0% {
  opacity: 0;
  transform: translateY(10px);
}

10% {
  opacity: 1;
  transform: translateY(0);
}

90% {
  opacity: 1;
  transform: translateY(0);
}

100% {
  opacity: 0.4;
  transform: translateY(0);
}
`;

export const NodeContent = styled.div`
  opacity: 0;
  width: 100%;
  transform: translateY(10px);
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  height: 29px;
  color: ${({ theme }) => theme.palette.hague100};
  animation: ${NodeContentFadeIn} 4s ease-out 1 forwards;
  animation-delay: ${({ position }) =>
    position && `${position * BASE_DELAY + SLIDE_UP_DELAY}ms`};
  margin-bottom: 33px;
  ${media.desktop`
    font-size: 21px;
    line-height: 28px;
  `}
`;

const subMessageFadeIn = keyframes`
0% {
  opacity: 0;
  transform: translateY(10px);
}

100% {
  opacity: 1;
  transform: translateY(0);
}
`;

export const SubMessage = styled.p`
  margin: 0;
  padding: 0;
  opacity: 0;
  transform: translateX(10px);
  display: inline-block;
  color: ${({ theme }) => theme.palette.hague100};
  font-weight: normal;
  animation: ${subMessageFadeIn} 0.5s ease-out 1 forwards;
  animation-delay: ${({ position }) =>
    position && `${position * BASE_DELAY + SUB_MESSAGE_DELAY}ms`};
  position: absolute;
  left: 28px;
  top: 20px;
  font-size: 16px;
  ${media.desktop`
    top: 24px;
    font-size:16px;
    line-height: 28px;
  `}
`;

const drawEdge = keyframes`
from {
  stroke-dasharray: 0, 100;
}
to {
  stroke-dasharray: 100, 100;
}
`;

const edgeStyle = css`
  position: absolute;
  left: 5px;
  top: 24px;
  animation: ${drawEdge} 1s linear 1 forwards;
  animation-delay: ${BASE_DELAY}ms;
  ${({ position }) => {
    return (
      position &&
      css`
        animation-delay: ${(position + 1) * BASE_DELAY}ms;
      `
    );
  }};
  stroke: ${({ theme }) => theme.palette.terracotta150};
  stroke-width: 1px;
  stroke-dasharray: 0, 100;
`;

export const Edge = ({ position }: { position?: number }) => {
  return (
    <svg
      css={edgeStyle}
      position={position}
      width="1"
      height="40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <line x1="0" y1="0" x2="0" y2="40" />
    </svg>
  );
};

export const Node = ({
  position,
  message,
  subMessage,
  hasEdge,
}: {
  position?: number,
  message: string,
  subMessage?: string,
  hasEdge?: boolean,
}) => (
  <div css="width: 100%">
    <Dot position={position} />
    <NodeContent position={position}>
      <Message>
        {message}
        <Ellipsis position={position} />
      </Message>
      {subMessage && <SubMessage position={position}>{subMessage}</SubMessage>}
      {hasEdge && <Edge position={position} />}
    </NodeContent>
  </div>
);
